/**
 * The settings index — Step 93.
 *
 * A grouped card grid, the shape the reference tool uses for its admin page
 * and the shape Kai asked for. Every card carries a one-line description,
 * because the hard part of a settings screen is not reaching a page, it is
 * knowing WHICH page: "Outbound mail" and "Inbound mailboxes" are one letter
 * apart in a tab strip and completely different jobs.
 *
 * Search filters across labels, descriptions AND keywords — the keywords
 * carry the reference tool's own names, so someone arriving from Freshservice
 * can type "supervisor rules" or "workflow automator" and land on ours.
 *
 * Planned destinations render greyed with their step number rather than being
 * hidden. Hiding them would make a half-built settings area look finished.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Check, Search, Settings2 } from 'lucide-react';
import { ADMIN_GROUPS, allAdminCards, cardMatches, type AdminCard } from './adminSections';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { apiFetch } from '../../lib/api';
import { qk } from '../../lib/queryClient';

/** Step 129 — a card's configured state: true, false, or undefined when the
 *  card has no probe or the probes have not loaded. */
type Probes = Record<string, boolean> | undefined;
const configuredState = (card: AdminCard, probes: Probes): boolean | undefined =>
  card.probe && probes ? probes[card.probe] : undefined;

function CardTile({ card, probes }: { card: AdminCard; probes: Probes }) {
  const configured = configuredState(card, probes);
  const inner = (
    <>
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
          card.to ? 'bg-brand/15 text-brand' : 'bg-muted text-muted-foreground',
        )}
      >
        <card.icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium">{card.label}</span>
          {configured === true && (
            <span
              className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white"
              title="Configured"
            >
              <Check className="h-2.5 w-2.5" />
            </span>
          )}
          {configured === false && (
            <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-px text-[10px] font-medium text-amber-800 dark:text-amber-300">
              Not set up
            </span>
          )}
          {!card.to && (
            <span className="rounded border px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Step {card.step}
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{card.description}</span>
      </span>
    </>
  );

  // Not built: deliberately NOT a link. A card that navigates to a 404 is
  // worse than one that plainly says it does not exist yet.
  if (!card.to) {
    return (
      <div
        className="flex cursor-default items-start gap-3 rounded-lg border border-dashed bg-muted/20 p-4 opacity-70"
        aria-disabled="true"
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={card.to}
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-card p-4 transition-colors',
        'hover:border-brand/40 hover:bg-accent/40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      {inner}
    </Link>
  );
}

export function AdminHomePage() {
  // URL state on purpose: filtering the settings index is a momentary act of
  // finding a card. The destination you then click is the shareable thing.
  const [query, setQuery] = useState('');

  const groups = useMemo(
    () =>
      ADMIN_GROUPS.map((g) => ({
        ...g,
        subgroups: g.subgroups
          .map((s) => ({ ...s, cards: s.cards.filter((c) => cardMatches(c, query)) }))
          .filter((s) => s.cards.length > 0),
      })).filter((g) => g.subgroups.length > 0),
    [query],
  );

  const total = allAdminCards().length;
  // Step 129 — "X of Y configured" per group, from one cheap probe call. The
  // page renders without it; the counters simply appear when it lands.
  const probesQuery = useQuery({
    queryKey: qk.adminConfigured,
    queryFn: () => apiFetch<{ probes: Record<string, boolean> }>('/api/admin/setup/configured'),
    staleTime: 30_000,
  });
  const probes = probesQuery.data?.probes;
  const progress = (g: (typeof groups)[number]) => {
    const cards = g.subgroups.flatMap((sg) => sg.cards).filter((c) => c.probe && c.to);
    if (!probes || cards.length === 0) return null;
    return { done: cards.filter((c) => probes[c.probe!]).length, total: cards.length };
  };
  const shown = groups.reduce((n, g) => n + g.subgroups.reduce((m, s) => m + s.cards.length, 0), 0);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
            <Settings2 className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold">Settings</h1>
            <p className="text-xs text-muted-foreground">Configure the service desk for this organisation.</p>
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings…"
            className="h-9 pl-8"
            aria-label="Search settings"
          />
        </div>
      </div>

      {shown === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Nothing in settings matches <span className="font-medium text-foreground">“{query}”</span>.
          </p>
          <button
            onClick={() => setQuery('')}
            className="mt-2 text-xs text-muted-foreground underline hover:text-foreground"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {query.trim() && (
            <p className="text-xs text-muted-foreground">
              {shown} of {total} settings match.
            </p>
          )}
          {groups.map((g) => (
            <section key={g.title}>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold">{g.title}</h2>
                {(() => {
                  const p = progress(g);
                  if (!p) return null;
                  const all = p.done === p.total;
                  return (
                    <span
                      className={cn(
                        'rounded-full px-2 py-px text-[11px] font-medium',
                        all
                          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                          : 'bg-muted text-muted-foreground',
                      )}
                      title="Cards with a configuration probe — the rest are always there"
                    >
                      {p.done} of {p.total} configured
                    </span>
                  );
                })()}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">{g.description}</p>
              <div className="space-y-4">
                {g.subgroups.map((s, i) => (
                  <div key={s.title ?? i}>
                    {s.title && (
                      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {s.title}
                      </p>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {s.cards.map((c) => (
                        <CardTile key={c.label} card={c} probes={probes} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
